import * as React from "react";
import Box from "@mui/material/Box";
import {
    AccordionActions,
    Button,
    Drawer,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import styled from "styled-components";
import type { TemplateItemInterface } from "src/components/template/types";
import {
    EditModes,
    useTemplateViewEdit,
} from "src/components/providers/template-view-edit-provider";
import Slider from "@mui/material/Slider";
import TemplateItemSample from "src/components/template/template-item-sample";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import PlaylistRemoveIcon from "@mui/icons-material/PlaylistRemove";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import TemplateSwitch from "src/components/template/template-switch";
import { useTemplateDrawer } from "src/components/providers/template-drawer-provider";
import cloneDeep from "lodash-es/cloneDeep";
import TemplateItemSearch from "src/components/template/template-item-search";
import JiraProjectSelector from "src/components/template/jira-project-selector";

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(0),
    "& .MuiPaper-root": {
        boxShadow: "none",
    },
}));

const CloseButton = styled(Button)`
    border-radius: 24px;
    font-size: 12px;
    width: 130px;
`;

const TemplateItemEditDrawer = () => {
    const [helperText, setHelperText] = React.useState<string>("");
    const [propertyValue, setPropertyValue] = React.useState<string>("issues");
    const [queryVariables, setQueryVariables] = React.useState<{ [label: string]: string }>({
        project: localStorage.getItem("project") || "All",
    });
    const [titles, setTitles] = React.useState<{ [label: string]: string }>({});
    const [searchItemQuery, setSearchItemQuery] = React.useState<string>("");
    const { editView, availableItems } = useTemplateViewEdit();
    const { drawerOpen, setDrawerOpen, onEditItem, setOnEditItem, template } = useTemplateDrawer();
    const { isChart, item, templateIndex, rowIndex, itemIndex } = onEditItem || {};

    const [sizing, setSizing] = React.useState<number>(
        item && "sizing" in item && item.sizing ? item.sizing.md : 12
    );
    const [expanded, setExpanded] = React.useState<string>("panel1");

    const items = React.useMemo(() => {
        return Object.keys(availableItems).reduce((acc, key) => {
            if ("sizing" in availableItems[key]) {
                if (isChart) {
                    acc[key] = availableItems[key];
                }
            } else if (!isChart) {
                acc[key] = availableItems[key];
            }
            return acc;
        }, {} as { [id: string]: TemplateItemInterface });
    }, [availableItems, isChart]);

    React.useEffect(() => {
        if (item && "sizing" in item) {
            setSizing(item.sizing.md);
        }
    }, [item]);

    React.useEffect(() => {
        if (item && "title" in item) {
            if (item.title.options && item.title.options.length) {
                setTitles(
                    item.title.options.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.text }), {})
                );
            } else {
                setTitles({ "0": item.title.value });
            }
        }
    }, [item]);

    const minSize = 3;

    const handleResize = (_event: Event, newValue: number) => {
        if (newValue > 12) {
            setHelperText("Can not be bigger than full width");
        } else if (newValue < minSize) {
            setHelperText("Can not be smaller than 1/3 of the width");
        } else {
            setHelperText("");
            if (item && newValue && "sizing" in item && newValue !== sizing) {
                if (editView) {
                    editView({
                        templateIndex: templateIndex as number,
                        rowIndex: rowIndex as number,
                        itemIndex: itemIndex as number,
                        item: {
                            ...(item as TemplateItemInterface),
                            sizing: { ...item.sizing, md: newValue },
                        },
                        editMode: EditModes.editItem,
                    });
                }
                setSizing(newValue);
            }
        }
    };

    const toggleDrawer = (event: { type: string; key: string }) => {
        if (event.type === "keydown" && event.key === "Escape") {
            setDrawerOpen(false);
        }
    };

    React.useEffect(() => {
        document.addEventListener("keydown", toggleDrawer, false);

        return () => {
            document.removeEventListener("keydown", toggleDrawer, false);
        };
    }, []);

    const renderTitle = () => {
        if (!titles || !Object.keys(titles).length) return null;

        const handleTitleSave = () => {
            if (editView && item && "title" in item) {
                const newItem: TemplateItemInterface = {
                    ...item,
                    title: {
                        ...item.title,
                        ...(Object.keys(titles).length > 1
                            ? {
                                  options: Object.keys(titles).reduce(
                                      (acc, k) => [...acc, { value: k, text: titles[k] }],
                                      []
                                  ),
                              }
                            : {
                                  value: titles[Object.keys(titles)[0]],
                              }),
                    },
                };
                editView({
                    templateIndex: templateIndex as number,
                    rowIndex: rowIndex as number,
                    itemIndex: itemIndex as number,
                    item: newItem,
                    editMode: EditModes.editItem,
                });
            }
        };

        return (
            <>
                <CloseButton startIcon={<ArrowBackIosIcon />} onClick={() => setDrawerOpen(false)}>
                    Close items
                </CloseButton>
                {Object.keys(titles).map((key) => (
                    <TextField
                        error={false}
                        id="outlined-basic"
                        label={
                            Object.keys(titles).length > 1 ? `Title for option: ${key}` : "Title"
                        }
                        variant="outlined"
                        size="small"
                        value={titles[key]}
                        helperText={false}
                        style={{ margin: "16px", minWidth: "284px" }}
                        onChange={(event) => {
                            setTitles((prevState) => ({
                                ...prevState,
                                [key]: event.target.value,
                            }));
                        }}
                        onKeyDown={(event) => {
                            if (event.type === "keydown" && event.key === "Enter") {
                                handleTitleSave();
                            }
                        }}
                        onBlur={() => {
                            handleTitleSave();
                        }}
                    />
                ))}
            </>
        );
    };

    const queryVariablesSetter = (currentItem: TemplateItemInterface, editing: boolean = false) => {
        if (!currentItem.query.variables || Object.keys(currentItem.query.variables).length === 0)
            return null;
        if (template?.queryVariablesSetter) return null;

        return Object.keys(currentItem.query.variables).map((variableName) => {
            if (variableName === "creator-assignee") {
                return (
                    <FormControl required sx={{ m: 1 }} fullWidth>
                        <InputLabel id="demo-simple-select-required-label">Tickets</InputLabel>
                        <Select
                            labelId="demo-simple-select-required-label"
                            id="demo-simple-select-required"
                            value={queryVariables[variableName] || ""}
                            label="Tickets *"
                            onChange={(event) => {
                                if (editing) {
                                    if (editView) {
                                        editView({
                                            templateIndex: templateIndex as number,
                                            rowIndex: rowIndex as number,
                                            itemIndex: itemIndex as number,
                                            item: {
                                                ...currentItem,
                                                query: {
                                                    ...currentItem.query,
                                                    variables: {
                                                        ...currentItem.query.variables,
                                                        [variableName]: event.target.value,
                                                    },
                                                },
                                            },
                                            editMode: EditModes.editItem,
                                        });
                                    }
                                }
                                setQueryVariables((prevState) => ({
                                    ...prevState,
                                    [variableName]: event.target.value,
                                }));
                            }}
                        >
                            <MenuItem value="creator">Created by this group</MenuItem>
                            <MenuItem value="assignee">Assigned to this group</MenuItem>
                            <MenuItem value="both">Both</MenuItem>
                        </Select>
                    </FormControl>
                );
            }
            if (variableName === "project") {
                return (
                    <JiraProjectSelector
                        queryVariablesSetter={
                            template?.queryVariablesSetter?.project || {
                                project: {
                                    dynamicOptions: "$data.projects[*].projectKey",
                                },
                            }
                        }
                        value={queryVariables?.project}
                        setValue={(value) => {
                            if (editing) {
                                if (editView) {
                                    editView({
                                        templateIndex: templateIndex as number,
                                        rowIndex: rowIndex as number,
                                        itemIndex: itemIndex as number,
                                        item: {
                                            ...currentItem,
                                            query: {
                                                ...currentItem.query,
                                                variables: {
                                                    ...currentItem.query.variables,
                                                    [variableName]: value,
                                                },
                                            },
                                        },
                                        editMode: EditModes.editItem,
                                    });
                                }
                            }
                            setQueryVariables((prevState) => ({
                                ...prevState,
                                [variableName]: value,
                            }));
                        }}
                    />
                );
            }
            return null;
        });
    };

    const propertySetter = (currentItem: TemplateItemInterface, editing: boolean = false) => {
        const defaultItem = availableItems[currentItem.id];
        if (!defaultItem) return null;
        if (!JSON.stringify(defaultItem).includes("$issues-points")) return null;
        if (template?.propertySetter) return null;

        return (
            <TemplateSwitch
                value={propertyValue}
                setValue={(val: string) => {
                    if (editing) {
                        if (editView) {
                            const changedItem = JSON.parse(
                                JSON.stringify(defaultItem).replace(/\$issues-points/g, val)
                            );
                            editView({
                                templateIndex: templateIndex as number,
                                rowIndex: rowIndex as number,
                                itemIndex: itemIndex as number,
                                item: {
                                    ...changedItem,
                                    query: currentItem.query,
                                    ...("sizing" in currentItem && { sizing: currentItem.sizing }),
                                },
                                editMode: EditModes.editItem,
                            });
                        }
                    }
                    setPropertyValue(val);
                }}
                options={[
                    {
                        name: "Issues",
                        value: "issues",
                    },
                    {
                        name: "Points",
                        value: "points",
                    },
                ]}
            />
        );
    };

    const editItem = () => {
        if (!item || !("id" in item)) return null;
        return (
            <Box
                sx={{
                    width: "487px",
                    backgroundColor: "#0e0e0e",
                    padding: "14px",
                }}
                role="presentation"
                onKeyDown={toggleDrawer}
            >
                <Stack direction="column" style={{ height: "100%" }}>
                    <Stack direction="column" p={2} style={{ width: "100%" }}>
                        {renderTitle()}
                        {queryVariablesSetter(item, true)}
                        {propertySetter(item, true)}
                        {"sizing" in item && (
                            <>
                                <Typography style={{ fontSize: "13px" }}>
                                    Adjust Chart Size
                                </Typography>
                                <Slider
                                    getAriaLabel={() => "Size of Item"}
                                    value={sizing}
                                    onChange={handleResize}
                                    valueLabelDisplay="auto"
                                    getAriaValueText={(value: number) => `${value}`}
                                    step={1}
                                    marks
                                    min={0}
                                    max={12}
                                />
                                {helperText && <span>{helperText}</span>}
                            </>
                        )}
                        <Button
                            startIcon={<PlaylistRemoveIcon />}
                            color="warning"
                            onClick={() => {
                                if (editView) {
                                    editView({
                                        templateIndex: templateIndex as number,
                                        rowIndex: rowIndex as number,
                                        itemIndex: itemIndex as number,
                                        editMode: EditModes.removeItem,
                                    });
                                }
                            }}
                        >
                            Remove Item
                        </Button>
                    </Stack>
                </Stack>
            </Box>
        );
    };

    const changeItem = () => (
        <Box
            sx={{
                width: "487px",
                backgroundColor: "#0e0e0e",
                padding: "14px",
            }}
            role="presentation"
            onKeyDown={toggleDrawer}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
                spacing={1}
                style={{ width: "100%" }}
            >
                {item && "id" in item ? (
                    <p style={{ fontSize: "13px" }}>Change {isChart ? "chart" : "number"}</p>
                ) : (
                    <p style={{ fontSize: "13px" }}>Insert {isChart ? "chart" : "number"}</p>
                )}
                <TemplateItemSearch setSearchItemQuery={setSearchItemQuery} />
            </Stack>
            {Object.keys(items)
                .filter((id) => {
                    if (!searchItemQuery) return true;
                    return id.toString().toLowerCase().includes(searchItemQuery.toLowerCase());
                })
                .map((id) => {
                    if (item && "id" in item && id === item.id) return null;
                    const [category, subCategory] = id.split(".");
                    let categoryName = "";
                    if (category === "git") {
                        categoryName = subCategory === "mrs" ? "PRs" : subCategory;
                    } else {
                        categoryName = category;
                    }
                    if (categoryName.length <= 2) {
                        categoryName = categoryName.toUpperCase();
                    } else {
                        categoryName = categoryName.charAt(0).toUpperCase() + categoryName.slice(1);
                    }
                    let sampleTitle =
                        items[id]?.title.value &&
                        items[id].title.value.replace(/ ?\(?\$[a-z-A-Z0-9.]*\)?/g, "");
                    if (items[id]?.title.options && items[id]?.title.options.length) {
                        sampleTitle = items[id]?.title.options[0].text;
                    }
                    let renderSample = cloneDeep(items[id]);
                    if (JSON.stringify(renderSample).includes("$issues-points")) {
                        renderSample = JSON.parse(
                            JSON.stringify(renderSample).replace(/\$issues-points/g, propertyValue)
                        );
                    }
                    const queryVariablesNotSet =
                        !template?.queryVariablesSetter &&
                        items[id].query.variables &&
                        Object.keys(items[id].query.variables).length &&
                        !!Object.keys(items[id].query.variables).find((v) => !queryVariables[v]);
                    const propertyNotSet =
                        !template?.propertySetter &&
                        JSON.stringify(items[id]).includes("$issues-points") &&
                        !propertyValue;
                    return (
                        <Accordion
                            expanded={expanded === id}
                            onChange={() => setExpanded(id)}
                            style={{ marginBottom: "16px", borderRadius: "8px", fontSize: "13px" }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel1-content"
                                id="panel1-header"
                            >
                                {categoryName ? `${categoryName} - ` : ""}
                                {sampleTitle}
                            </AccordionSummary>
                            <AccordionDetails>
                                {expanded === id && (
                                    <>
                                        <TemplateItemSample item={renderSample} />
                                        {queryVariablesSetter(items[id])}
                                        {propertySetter(items[id])}
                                    </>
                                )}
                            </AccordionDetails>
                            <AccordionActions>
                                <Button
                                    disabled={queryVariablesNotSet || propertyNotSet || false}
                                    onClick={() => {
                                        let newItem: TemplateItemInterface = items[id];
                                        if (
                                            !template?.propertySetter &&
                                            JSON.stringify(items[id]).includes("$issues-points") &&
                                            propertyValue
                                        ) {
                                            newItem = JSON.parse(
                                                JSON.stringify(items[id]).replace(
                                                    /\$issues-points/g,
                                                    propertyValue
                                                )
                                            );
                                        }
                                        newItem = {
                                            ...newItem,
                                            query: {
                                                ...newItem.query,
                                                ...(newItem.query.variables && {
                                                    variables: Object.keys(
                                                        newItem.query.variables
                                                    ).reduce(
                                                        (acc, key) => ({
                                                            ...acc,
                                                            [key]:
                                                                template?.queryVariablesSetter &&
                                                                template?.queryVariablesSetter[
                                                                    key
                                                                ] &&
                                                                newItem.query.variables &&
                                                                newItem.query.variables[key]
                                                                    ? newItem.query.variables[key]
                                                                    : queryVariables[key],
                                                        }),
                                                        {}
                                                    ),
                                                }),
                                            },
                                            ...(item &&
                                                "sizing" in item &&
                                                "sizing" in newItem && {
                                                    sizing: {
                                                        ...newItem.sizing,
                                                        md: item.sizing.md,
                                                    },
                                                }),
                                        };
                                        if (editView) {
                                            editView({
                                                templateIndex: templateIndex as number,
                                                rowIndex: rowIndex as number,
                                                itemIndex: itemIndex as number,
                                                item: newItem,
                                                editMode: EditModes.addItem,
                                            });
                                            setOnEditItem({
                                                isChart: "sizing" in newItem,
                                                item: newItem,
                                                templateIndex,
                                                rowIndex,
                                                itemIndex,
                                            });
                                        }
                                    }}
                                >
                                    Select
                                </Button>
                            </AccordionActions>
                        </Accordion>
                    );
                })}
        </Box>
    );

    return (
        <React.Fragment key="right">
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={toggleDrawer}
                variant="persistent"
                onKeyDown={toggleDrawer}
                sx={{ paddingBottom: "30px", marginBottom: "30px" }}
            >
                {editItem()}
                {changeItem()}
            </Drawer>
        </React.Fragment>
    );
};

export default TemplateItemEditDrawer;
